import Vue from "vue";
import axios from "axios";
import { SessionStorage } from 'quasar'
import { getAccessTokenFromCookie } from 'src/utils/cookies'
import { sendUnauthorizedEvent, RETRY_DELAY_MS } from 'src/utils/retry-with-token'

/**
 * Sleep for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const getAuthHeaders = () => {
  const headers = {};

  // PRIORITY 1: Try cookie first (always fresh from parent app)
  let accessToken = getAccessTokenFromCookie();

  if (accessToken) {
    console.log('✅ [Axios] Using token from cookie');
    // Sync cookie to SessionStorage for consistency
    SessionStorage.set('token', accessToken);
  } else {
    // PRIORITY 2: Fallback to SessionStorage (might be stale or from URL param)
    accessToken = SessionStorage.getItem("token");
    if (accessToken) {
      console.log('⚠️ [Axios] Using token from SessionStorage (cookie not available)');
    }
  }

  if (accessToken) {
    headers['access_token'] = accessToken;
  }

  return headers;
}

// request interceptor
axios.interceptors.request.use(
  config => {
    const authHeaders = getAuthHeaders();
    config.url = config.url;
    config.headers = { ...config.headers, ...authHeaders };
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// response interceptor - Handle 401 Unauthorized errors with retry
axios.interceptors.response.use(
  response => {
    return response.data.body ? response.data.body : response.data;
  },
  async function (error) {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors with retry mechanism
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      console.warn('[Axios] First attempt failed with 401, notifying parent and retrying...');

      // Mark request as retried to prevent infinite loops
      originalRequest._retry = true;

      // Send unauthorized event to parent
      sendUnauthorizedEvent();

      // Wait 5 seconds for parent to refresh token in cookie
      await sleep(RETRY_DELAY_MS);

      // Get fresh token from cookie
      const freshToken = getAccessTokenFromCookie();

      if (freshToken) {
        console.log('[Axios] Retrying request with fresh token from cookie...');
        // Update the authorization header with fresh token
        originalRequest.headers['access_token'] = freshToken;
        // Sync to SessionStorage
        SessionStorage.set('token', freshToken);
        // Retry the request
        return axios(originalRequest);
      } else {
        console.error('[Axios] No fresh token available after retry delay');
        // Clear session and redirect to logout if still no token
        SessionStorage.clear();
        // Optionally redirect: window.location = "/logout";
      }
    }

    // Handle other error status codes
    if (error.response) {
      switch (error.response.status) {
        case 400:
          console.log("Something happened: ", error.response.status);
          break;
        case 500:
          console.log("Something happened: ", error.response.status);
          break;
        default:
          console.log("Something happened: ", error.response.status);
          break;
      }
    }

    // return Error object with Promise
    return Promise.reject(error);
  }
);

Vue.prototype.$axios = axios;



